/**
 * NBDY PDP entrance stagger.
 *
 * Runs after the cross-document View Transition (product card -> PDP) has
 * completed. The CSS view-transition handles the image morph and a single
 * fade/slide of the `[data-view-transition-type="product-details"]`
 * container as a whole. This script layers a quick stagger on its direct
 * children so the page "settles in" rather than appearing all at once.
 *
 * Activation conditions (all must hold):
 *  1. `prefers-reduced-motion: no-preference`
 *  2. Not a low-power device (mirrors `view-transitions.js` threshold)
 *  3. The current `pagereveal` is a product-image-transition (i.e. the
 *     user arrived by clicking a product card, not by direct nav)
 *
 * Easy to disable: remove the `<script src=".../nbdy-pdp-enter.js">` tag
 * in `snippets/scripts.liquid`. The CSS transition will continue working.
 */
import { animate, stagger } from '@theme/anime';

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const LOW_POWER = Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;

const INITIAL_OPACITY = 0.85;
const INITIAL_TRANSLATE_PX = 4;

if (!REDUCED_MOTION && !LOW_POWER) {
  window.addEventListener('pagereveal', async (event) => {
    const viewTransition = /** @type {any} */ (event).viewTransition;
    if (!viewTransition) return;

    const customType = sessionStorage.getItem('custom-transition-type');
    if (customType !== 'product-image-transition') return;

    const details = document.querySelector('[data-view-transition-type="product-details"]');
    if (!details) return;

    const candidateSelectors = [
      ':scope > .group-block > *',
      ':scope > * > .group-block',
      ':scope > *',
    ];

    let targets = [];
    for (const sel of candidateSelectors) {
      const found = Array.from(details.querySelectorAll(sel)).filter((el) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
      if (found.length >= 2) {
        targets = found;
        break;
      }
    }
    if (!targets.length) return;

    // Set the start state BEFORE awaiting — while the view-transition overlay
    // is still covering the live DOM. Tiny deltas (0.85 opacity, 4px offset)
    // keep any residual snap from the snapshot end-state imperceptible.
    for (const el of targets) {
      el.style.opacity = String(INITIAL_OPACITY);
      el.style.transform = `translateY(${INITIAL_TRANSLATE_PX}px)`;
      el.style.willChange = 'opacity, transform';
    }

    try {
      await viewTransition.finished;
    } catch (_) {
      // Transition was skipped — reset and bail.
      for (const el of targets) {
        el.style.removeProperty('opacity');
        el.style.removeProperty('transform');
        el.style.removeProperty('will-change');
      }
      return;
    }

    const anim = animate(targets, {
      opacity: [INITIAL_OPACITY, 1],
      translateY: [INITIAL_TRANSLATE_PX, 0],
      duration: 260,
      delay: stagger(40),
      ease: 'cubicBezier(0.22, 0.61, 0.36, 1)',
    });

    anim.then(() => {
      for (const el of targets) {
        el.style.removeProperty('opacity');
        el.style.removeProperty('transform');
        el.style.removeProperty('will-change');
      }
    });
  });
}
